import { useEffect, useState } from 'react';

/**
 * Custom hook to detect user's motion preferences.
 * Respects the prefers-reduced-motion media query for accessibility.
 * 
 * @returns {boolean} true if user prefers reduced motion
 */
export function useReducedMotion(): boolean {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    // Check if window is available (SSR safety)
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    
    // Set initial value
    setPrefersReducedMotion(mediaQuery.matches);

    // Listen for changes
    const handleChange = (event: MediaQueryListEvent) => {
      setPrefersReducedMotion(event.matches);
    };

    // Modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
    
    // Fallback for older browsers
    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  return prefersReducedMotion;
}

/**
 * Returns animation props that respect reduced motion preferences.
 * Use this to wrap framer-motion animations.
 */
export function useMotionSafe() {
  const prefersReducedMotion = useReducedMotion();

  return {
    // If reduced motion is preferred, return empty/instant animations
    initial: prefersReducedMotion ? false : undefined,
    animate: prefersReducedMotion ? false : undefined,
    transition: prefersReducedMotion ? { duration: 0 } : undefined,
    
    // Helper to conditionally apply animation props
    safeAnimate: <T extends object>(props: T): T | {} => 
      prefersReducedMotion ? {} : props,
  };
}

export default useReducedMotion;
